import { DEFAULT_LOCALE, LOCALES, type Locale } from '@aysar/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useForm,
  useWatch,
  type FieldErrors,
  type FieldValues,
  type Path,
  type Resolver,
  type UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type ZodType } from 'zod';

import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';
import { slugify } from '@/shared/utils/slugify/slugify.util';

import { buildDefaultValues } from '../build-default-values/build-default-values';
import { isCancelledSubmit } from '../cancelled-submit/cancelled-submit';
import { flattenErrorPaths, setErrorAtPath, unownedErrorPaths } from '../error-paths/error-paths';
import {
  stripEmptyFields,
  stripHiddenFields,
  stripHiddenSections,
} from '../hidden-fields/hidden-fields';
import { invalidSectionIdsOf } from '../invalid-sections/invalid-sections';
import {
  TRANSLATIONS_KEY,
  fieldPaths,
  firstInvalidLocale,
  focusablePaths,
  localesWithErrors,
  localizedPath,
} from '../localized-form/localized-form';
import { flattenFields, type ResolvedSection } from '../resolve-sections/resolve-sections';
import { mapServerErrors } from '../server-error-map/server-error-map';
import { type FieldDef, type SlugFieldDef } from '../types/dynamic-form.types';

import { type DynamicFormOutcome } from './DynamicForm.types';

export interface UseDynamicFormLogicInput<TValues extends FieldValues> {
  /** The layout, already resolved — sections however the caller authored them. */
  sections: readonly ResolvedSection[];
  /**
   * Input and output are BOTH `TValues`: `zodResolver` requires the schema's
   * input to be the form's value shape, and a contract schema carrying a
   * default or a transform would silently mean the form edits one shape while
   * the resolver validates another.
   */
  schema: ZodType<TValues, TValues>;
  initialValues?: unknown;
  onSubmit: (values: TValues) => Promise<void>;
  /** The tab the form OPENS on, when it owns its own locale. */
  defaultLocale?: Locale;
  /**
   * The tab the form is on — supplied by a host that has a **second region**
   * following the same content language (the question editor's choices table,
   * P10.6f's member preview).
   *
   * Supplying it makes the locale controlled: the form stops moving on its own
   * and asks through `onLocaleChange` instead — including when a failed submit
   * needs to reveal the offending translation, which is the one case a host
   * **must** honour or Save becomes a dead button again.
   */
  locale?: Locale;
  onLocaleChange?: (locale: Locale) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  /**
   * The whole form, on every change — for a region **outside** the `<form>` that
   * depends on one of its values.
   *
   * The question editor's choices table is the case: whether a question has
   * choices at all is `isOptionBacked(values.type)`, and the table sits in the
   * editor's other column where no `visibleWhen` can reach it. Deliberately not
   * on `DynamicFormHandle` — a ref changes nothing on screen, and a caller that
   * DRAWS from a value needs it as state, which is the same argument that put
   * `onDirtyChange` here rather than there.
   *
   * Must be stable (`useCallback`) — it is an effect dependency.
   */
  onValuesChange?: (values: Record<string, unknown>) => void;
}

export interface UseDynamicFormLogic<TValues extends FieldValues> {
  form: UseFormReturn<TValues, unknown, TValues>;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Locale tabs holding at least one invalid field. */
  invalidLocales: Locale[];
  /** Sections holding at least one invalid field — a folded one must reopen. */
  invalidSectionIds: readonly string[];
  /** The whole form's values, subscribed once for every section's `visibleWhen`. */
  values: Record<string, unknown>;
  /** Messages nothing on screen owns. */
  formErrors: string[];
  isSubmitting: boolean;
  submit: (event: React.FormEvent) => void;
  /** The same submit, driven by a host, resolving what happened (never throwing). */
  submitProgrammatically: () => Promise<DynamicFormOutcome>;
  /** Back to the mounted values, or onto new ones. Clears server errors too. */
  reset: (values?: unknown) => void;
  /** The first invalid field, else the first field — switching locale if it must. */
  focusFirstProblem: () => void;
  /** Resolves a field to its form path, expanding a translatable one per locale. */
  pathFor: (field: FieldDef, locale: Locale) => Path<TValues>;
  /** The path a slug field derives from, for the given locale. */
  sourcePathFor: (field: SlugFieldDef, locale: Locale) => Path<TValues> | undefined;
  markSlugManual: (name: string) => void;
  handleAfterChange: (path: string, value: unknown) => void;
  /**
   * A control saying its own TEXT is unreadable — so it emitted no value and the
   * schema is about to validate the last one that parsed.
   */
  reportParseFailure: (path: string, isUnparsed: boolean) => void;
}

function isSlugField(field: FieldDef): field is SlugFieldDef {
  return field.kind === 'slug';
}

/**
 * The form's behaviour, kept out of the markup (U5/M2).
 *
 * Three decisions carry most of the weight.
 *
 * **`handleSubmit` is given an `onInvalid` handler.** Without one, a required
 * English name failing while the Arabic tab is on screen produces a Save button
 * that does nothing, no visible error, and no clue where to look — the single
 * worst failure mode a translatable form has. `onInvalid` switches to the
 * offending tab, and only to a tab: when the problem is outside the translations
 * block, changing tabs would hide the real error rather than reveal it.
 *
 * **A slug tracks its source through the source's CHANGE HANDLER**, not through
 * an effect watching it. An effect that wrote the form on every render of a
 * sibling field would both violate `set-state-in-effect` and race the operator's
 * own typing. The moment the key is edited by hand it stops tracking, because
 * from then on the operator is the authority on it.
 *
 * **A server rejection is mapped by dotted path**, and anything naming a path
 * this form does not render is raised to form level rather than dropped.
 */
export function useDynamicFormLogic<TValues extends FieldValues>({
  sections,
  schema,
  initialValues,
  onSubmit,
  defaultLocale = DEFAULT_LOCALE,
  locale: controlledLocale,
  onLocaleChange,
  onDirtyChange,
  onValuesChange,
}: UseDynamicFormLogicInput<TValues>): UseDynamicFormLogic<TValues> {
  const { t } = useTranslation('uiKit');
  const [ownLocale, setOwnLocale] = useState<Locale>(defaultLocale);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const locale = controlledLocale ?? ownLocale;

  /**
   * Moves the tab, or asks the host to.
   *
   * The uncontrolled half still calls `onLocaleChange`, so a host may *observe*
   * the locale without taking ownership of it — a preview that follows the form
   * needs the value, not the authority over it.
   */
  const setLocale = useCallback(
    (next: Locale) => {
      if (controlledLocale === undefined) {
        setOwnLocale(next);
      }

      onLocaleChange?.(next);
    },
    [controlledLocale, onLocaleChange],
  );
  const manualSlugs = useRef(new Set<string>());
  /**
   * The field a failed submit wanted to focus but could not reach, because it
   * lives in a translation that was not on screen.
   *
   * A ref rather than state: it is read in an effect after the locale has
   * changed, never during render (`react-hooks/refs`), and writing state here
   * would re-render the form for something nothing draws.
   */
  const pendingFocus = useRef<Path<TValues> | null>(null);

  /**
   * Paths whose control currently holds text it could not read.
   *
   * A ref, not state: the resolver reads it at submit time and nothing on screen
   * draws it, so re-rendering the whole form on every keystroke inside a JSON
   * editor would buy nothing.
   */
  const unparsedPaths = useRef(new Set<string>());

  const reportParseFailure = useCallback((path: string, isUnparsed: boolean) => {
    if (isUnparsed) {
      unparsedPaths.current.add(path);
      return;
    }

    unparsedPaths.current.delete(path);
  }, []);

  // The flat field list every value-layer concern works from. Grouping is a
  // layout fact and must not reach defaults, paths, stripping or error mapping.
  const fields = useMemo(() => flattenFields(sections), [sections]);

  const defaultValues = useMemo(
    () => buildDefaultValues(fields, initialValues),
    [fields, initialValues],
  );

  /**
   * The schema resolver, with the values of `visibleWhen`-hidden fields removed
   * FIRST.
   *
   * Stripping here rather than at submit is what makes a conditional field safe:
   * the resolver's returned values are what RHF hands to the submit handler, so
   * one strip covers both validation and the payload. Doing it only at submit
   * would leave a hidden field's stale value to fail a `.strict()` contract
   * schema, and the error would land on a control that is not on screen.
   */
  const resolver = useMemo(() => {
    const base = zodResolver(schema);

    return (async (values, context, options) => {
      // Sections first: a hidden SECTION hides fields whose own predicate says
      // nothing, and a `.strict()` schema cannot tell the two cases apart.
      const withoutHiddenSections = stripHiddenSections(
        sections,
        values as Record<string, unknown>,
      );

      const result = await base(
        stripEmptyFields(fields, stripHiddenFields(fields, withoutHiddenSections)) as TValues,
        context,
        options,
      );

      if (unparsedPaths.current.size === 0) return result;

      /*
       * A control whose own text does not parse emits NOTHING, so the schema
       * above just validated the last document that did and answered "fine".
       * Folding the refusal in here rather than through `form.setError` is the
       * only way it survives: the resolver replaces `errors` wholesale on every
       * submit, which is what made the first attempt at this a no-op.
       */
      let errors = result.errors;

      for (const path of unparsedPaths.current) {
        errors = setErrorAtPath(errors, path, {
          type: 'unparsed',
          message: t('json.blocksSave'),
        });
      }

      // RHF's own convention for a failed resolve: no values reach the submit
      // handler when anything refused.
      return { values: {}, errors };
      // `t` is a dependency: the message is rendered from it, and a resolver
      // holding a stale translator would print the old language after a switch.
      // Rebuilding it costs nothing — RHF reads the resolver off its options on
      // every validate rather than capturing the one it was mounted with.
    }) satisfies Resolver<TValues, unknown, TValues>;
  }, [fields, schema, sections, t]);

  const form = useForm<TValues, unknown, TValues>({
    resolver,
    defaultValues: defaultValues as never,
    // Validate on the first submit, then live. Validating before the operator
    // has finished typing marks a half-written field red and teaches them to
    // ignore the colour.
    mode: 'onTouched',
  });

  /**
   * The whole form, subscribed ONCE.
   *
   * Every section's grid needs it for `visibleWhen`, and a subscription per grid
   * would be one re-render per grid per keystroke. Subscribed rather than read
   * with `getValues`, which goes stale — the P9 slug-field defect.
   */
  const watched: unknown = useWatch({ control: form.control });

  const paths = useMemo(() => fieldPaths(fields), [fields]);

  const pathFor = useCallback(
    (field: FieldDef, forLocale: Locale): Path<TValues> =>
      (field.isTranslatable === true
        ? localizedPath(field.name, forLocale)
        : field.name) as Path<TValues>,
    [],
  );

  const sourcePathFor = useCallback(
    (field: SlugFieldDef, forLocale: Locale): Path<TValues> | undefined => {
      const source = fields.find((candidate) => candidate.name === field.sourceName);

      return source === undefined ? undefined : pathFor(source, forLocale);
    },
    [fields, pathFor],
  );

  const markSlugManual = useCallback((name: string) => {
    manualSlugs.current.add(name);
  }, []);

  const handleAfterChange = useCallback(
    (changedPath: string, value: unknown) => {
      if (typeof value !== 'string') {
        return;
      }

      for (const field of fields) {
        if (!isSlugField(field) || manualSlugs.current.has(field.name)) {
          continue;
        }

        /*
         * A DISABLED slug is never derived — it is not the operator's to change
         * and it is not ours either.
         *
         * The question key is locked after creation because it is the address of
         * every stored answer. Deriving it anyway meant that typing in the
         * Arabic question label rewrote it, `slugify` of Arabic text is `''`
         * (there is no Latin to derive), and the form then refused to save with
         * "required" on a field the operator could not even focus. Two of the
         * three reasons the question form could not save at all between P10 and
         * P10.6d, and invisible to every spec because they called the submit
         * handler directly.
         */
        if (field.isDisabled === true) {
          continue;
        }

        const source = fields.find((candidate) => candidate.name === field.sourceName);

        if (source === undefined || pathFor(source, locale) !== changedPath) {
          continue;
        }

        form.setValue(
          pathFor(field, locale),
          slugify(value, { separator: field.separator, maxLength: field.maxLength }) as never,
          { shouldDirty: true, shouldValidate: form.formState.isSubmitted },
        );
      }
    },
    [fields, form, locale, pathFor],
  );

  /**
   * Remembers the first invalid field inside the locale being switched TO.
   *
   * Without it, focus-on-error is a no-op exactly when it matters most: RHF
   * calls `ref.focus()` while the offending control is still unmounted (its
   * translation is not on screen), so the form switches tab, reveals the field
   * and leaves the caret nowhere. The effect below focuses it once it exists.
   */
  const rememberFocus = useCallback((errors: FieldErrors, target: Locale, current: Locale) => {
    if (target === current) {
      // Nothing to wait for: the control is already mounted, RHF's own
      // `shouldFocusError` reached it, and leaving a path here would fire on
      // the operator's NEXT tab switch against an error from this one.
      pendingFocus.current = null;
      return;
    }

    const [first] = flattenErrorPaths(errors).filter((error) =>
      error.path.startsWith(`${TRANSLATIONS_KEY}.${target}.`),
    );

    pendingFocus.current = first === undefined ? null : (first.path as Path<TValues>);
  }, []);

  useEffect(() => {
    const target = pendingFocus.current;
    if (target === null) return;

    pendingFocus.current = null;
    form.setFocus(target);
    // Keyed on the locale: the control only exists once the tab has switched.
  }, [form, locale]);

  const applyServerError = useCallback(
    (error: ApiError) => {
      const mapped = mapServerErrors(error, paths);

      for (const fieldError of mapped.fieldErrors) {
        form.setError(fieldError.path as Path<TValues>, {
          type: 'server',
          message: fieldError.message,
        });
      }

      setFormErrors(mapped.formErrors);

      // Land the operator on the tab that actually has a problem.
      const target = firstInvalidLocale(form.formState.errors);
      if (target !== null) {
        setLocale(target);
        rememberFocus(form.formState.errors, target, locale);
      }
    },
    [form, locale, paths, rememberFocus, setLocale],
  );

  /**
   * The valid half, as an OUTCOME rather than as a side effect.
   *
   * A declined confirmation short-circuits before `applyServerError`: it is not
   * a refusal, there is nothing to map onto a field, and printing an error over
   * an edit the operator chose not to save is worse than doing nothing.
   */
  const runValid = useCallback(
    async (values: TValues): Promise<DynamicFormOutcome> => {
      setFormErrors([]);

      try {
        await onSubmit(values);

        return { status: 'saved' };
      } catch (error) {
        if (isCancelledSubmit(error)) {
          return { status: 'cancelled' };
        }

        applyServerError(error as ApiError);

        return { status: 'refused', error: error as ApiError };
      }
    },
    [applyServerError, onSubmit],
  );

  const handleValid = useCallback(
    async (values: TValues) => {
      await runValid(values);
    },
    [runValid],
  );

  const handleInvalid = useCallback(
    (errors: FieldErrors) => {
      // The `onInvalid` half: without it, Save is a dead button whenever the
      // failure is inside a locale that is not on screen — or, worse, on a path
      // no control renders at all, where there is nothing to open.
      setFormErrors(
        unownedErrorPaths(errors, paths).map(
          (error) => `${error.path}: ${t('field.invalidValue')}`,
        ),
      );

      const target = firstInvalidLocale(errors);
      if (target !== null) {
        setLocale(target);
        rememberFocus(errors, target, locale);
      }
    },
    [locale, paths, rememberFocus, setLocale, t],
  );

  /**
   * `handleSubmit` is composed at EVENT time, not during render.
   *
   * Both halves reach the pending-focus ref, and handing a ref-touching function
   * to another function while rendering is what `react-hooks/refs` forbids —
   * correctly, since the compiler cannot know `handleSubmit` will not call it
   * immediately. Composing inside the handler costs one allocation per submit
   * and keeps the rule satisfied honestly rather than with a suppression.
   */
  const submit = useCallback(
    (event: React.FormEvent) => {
      void form.handleSubmit(handleValid, handleInvalid)(event);
    },
    [form, handleInvalid, handleValid],
  );

  /**
   * The host-driven submit.
   *
   * `handleSubmit` resolves whichever branch it took, so the outcome is captured
   * in a local and read after it settles — RHF gives the caller no other way to
   * learn which half ran. It starts as `invalid` because that is the branch a
   * `handleSubmit` that calls NEITHER handler took (it cannot, but a default of
   * "saved" would be a lie if it ever did).
   */
  const submitProgrammatically = useCallback(async (): Promise<DynamicFormOutcome> => {
    let outcome: DynamicFormOutcome = { status: 'invalid' };

    await form.handleSubmit(
      async (values) => {
        outcome = await runValid(values);
      },
      (errors) => {
        handleInvalid(errors);
        outcome = { status: 'invalid' };
      },
    )();

    return outcome;
  }, [form, handleInvalid, runValid]);

  /**
   * Back to a known set of values.
   *
   * The values go through `buildDefaultValues` exactly as `initialValues` does —
   * a caller hands over what the SERVER holds, not the form's internal shape, and
   * a translatable form's shape (`translations.<locale>.<field>`) is built here or
   * nowhere. The server errors go with it: an edit that has been discarded must
   * not still be marked red for a submit that no longer describes it.
   */
  const reset = useCallback(
    (next?: unknown) => {
      setFormErrors([]);
      form.reset((next === undefined ? defaultValues : buildDefaultValues(fields, next)) as never);
    },
    [defaultValues, fields, form],
  );

  /**
   * Points the operator at the first thing that is wrong — or, when nothing is,
   * at the top of the form.
   *
   * A host that saved twelve editors and had one refused has to be able to say
   * *which*, and "scroll and look for red" is not that. The locale detour is the
   * same one a failed submit takes: a control in a translation that is not on
   * screen cannot be focused until the tab has switched.
   */
  const focusFirstProblem = useCallback(() => {
    const candidates = focusablePaths(fields);
    // `getFieldState` reads the form's LIVE error state; `formState.errors` is a
    // per-render snapshot, and a host that calls this straight after an awaited
    // submit would read the snapshot from before the refusal and confidently
    // focus the first field instead of the broken one.
    const target =
      candidates.find((candidate) => form.getFieldState(candidate.path as Path<TValues>).invalid) ??
      candidates[0];

    if (target === undefined) return;

    if (target.locale !== null && target.locale !== locale) {
      // The control does not exist yet — the effect below focuses it once the
      // tab has switched, exactly as a failed submit does.
      setLocale(target.locale);
      pendingFocus.current = target.path as Path<TValues>;

      return;
    }

    form.setFocus(target.path as Path<TValues>);
  }, [fields, form, locale, setLocale]);

  const isDirty = form.formState.isDirty;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Memoized on the watched object rather than rebuilt: the `{}` fallback is a
  // FRESH object every render, so an effect depending on `values` would fire on
  // every one of them — including `onValuesChange`, which a host turns into
  // state.
  const values = useMemo<Record<string, unknown>>(
    () =>
      typeof watched === 'object' && watched !== null ? (watched as Record<string, unknown>) : {},
    [watched],
  );

  useEffect(() => {
    onValuesChange?.(values);
  }, [onValuesChange, values]);

  return {
    form,
    locale,
    setLocale,
    invalidLocales: localesWithErrors(form.formState.errors),
    invalidSectionIds: invalidSectionIdsOf(sections, form.formState.errors, LOCALES),
    values,
    formErrors,
    isSubmitting: form.formState.isSubmitting,
    submit,
    submitProgrammatically,
    reset,
    focusFirstProblem,
    pathFor,
    sourcePathFor,
    markSlugManual,
    handleAfterChange,
    reportParseFailure,
  };
}
